import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Box, Card, CircularProgress, ClassNameMap, IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';
import CustomSearch from '../../../components/General/CustomSearch';
import CustomTable from '../../../components/General/CustomTable';
import useStyles from '../styles';
import CustomModal from '../../../components/General/CustomModal';
import apiClient from '../../../requests/api/apiClient';
import { GetSpeciesResponse } from '../../../requests/api/apiTypes';
import useFetchApi from '../../../hooks/useFetchApi';
import { FetchError } from '../../types';
import UpsertModalSpeciesContent from './UpsertModalSpeciesContent';
import DeleteSpeciesModalContent from './DeleteSpeciesModalContent';

const SPECIES_LIST_UI = [
  { label: 'Nombre', key: 'name' },
  { label: 'Acciones', key: 'actions' },
];

export type SpeciesSectionHandle = {
  openCreate: () => void;
};

type Props = {
  canEdit: boolean;
  canDelete: boolean;
};

const twoButtonsAction = (
  classes: ClassNameMap<string>,
  rowId: string,
  setUpsertModal: ({ open, id }: { open: boolean, id: string }) => void,
  setOpenDeleteModal: ({ open, id }: { open: boolean, id: string }) => void,
  canEdit: boolean,
  canDelete: boolean,
) => (
  <Box display='flex' alignItems='center' justifyContent='center'>
    {canEdit && (
      <IconButton onClick={() => setUpsertModal({ open: true, id: rowId })}>
        <EditIcon className={classes.icons} />
      </IconButton>
    )}
    {canDelete && (
      <IconButton onClick={() => setOpenDeleteModal({ open: true, id: rowId })}>
        <DeleteIcon className={classes.icons} />
      </IconButton>
    )}
  </Box>
);

const SpeciesSection = forwardRef<SpeciesSectionHandle, Props>((
  { canEdit, canDelete },
  ref,
) => {
  const classes = useStyles();
  const [paginationParameters, setPaginationParameters] = useState({ page: 0, rowsPerPage: 5 });
  const [query, setQuery] = useState('');
  const [upsertModal, setUpsertModal] = useState({ id: '', open: false });
  const [openDeleteModal, setOpenDeleteModal] = useState({ id: '', open: false });
  const [updateData, setUpdateData] = useState(false);
  const [isLoadingUpsert, setIsLoadingUpsert] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useImperativeHandle(ref, () => ({
    openCreate: () => setUpsertModal({ id: '', open: true }),
  }));

  const { data: fetchData, loading } = useFetchApi<GetSpeciesResponse>({
    modelName: 'species',
    modelEndpoint: 'speciesList',
    dependencies: [
      paginationParameters.page, paginationParameters.rowsPerPage, query, updateData,
    ],
    inputData: {
      page: paginationParameters.page,
      limit: paginationParameters.rowsPerPage,
      searchValue: query,
    },
  });

  const headersUi = SPECIES_LIST_UI.map((c) => (
    c.key === 'actions' ? { ...c, className: classes.tableHeaderStyle } : c
  ));

  const localData = {
    count: fetchData?.totalSpecies ?? fetchData?.species?.length ?? 0,
    values: fetchData?.species?.map((elem) => ({
      id: elem.id ?? uuidv4(),
      name: elem.name ?? '',
      actions: twoButtonsAction(
        classes,
        elem.id as string,
        setUpsertModal,
        setOpenDeleteModal,
        canEdit,
        canDelete,
      ),
    })) ?? [],
  };

  const handleQuery = (valueToSearch: string) => {
    setQuery(valueToSearch);
    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
  };

  const handleCloseModal = () => setUpsertModal({ id: '', open: false });
  const handleDeleteCloseModal = () => setOpenDeleteModal({ id: '', open: false });

  const handleDeleteFunction = async ({ id }: { id: string }) => {
    try {
      setIsLoadingDelete(true);
      const { data } = await apiClient.species.speciesDelete(id);
      setUpdateData((p) => !p);
      handleDeleteCloseModal();
      enqueueSnackbar(data.message, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const handleUpsertFunction = async ({ name, id }: { name: string, id?: string }) => {
    try {
      let response;
      setIsLoadingUpsert(true);
      if (id) {
        response = await apiClient.species.speciesUpdate(id, { name });
      } else {
        response = await apiClient.species.speciesCreate({ name });
      }
      setUpdateData((p) => !p);
      handleCloseModal();
      enqueueSnackbar(response.data.message, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsLoadingUpsert(false);
    }
  };

  return (
    <Box mt={3}>
      {loading ? (
        <CircularProgress
          size={'3rem'}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '60%',
            marginTop: '-12px',
            marginLeft: '-12px',
          }}
        />
      ) : (
        <Card>
          <CustomSearch query={query} handleQuery={handleQuery} />
          <CustomTable
            rows={localData}
            headers={headersUi}
            paginationParameters={paginationParameters}
            setPaginationParameters={setPaginationParameters}
          />
        </Card>
      )}
      <CustomModal
        open={upsertModal.open}
        handleClose={() => setUpsertModal({ id: '', open: false })}
        data={{ id: upsertModal.id }}
      >
        <UpsertModalSpeciesContent
          handleCloseModal={handleCloseModal}
          handleUpsertFunction={handleUpsertFunction}
          isLoadingUpsert={isLoadingUpsert}
        />
      </CustomModal>
      <CustomModal
        open={openDeleteModal.open}
        handleClose={() => setOpenDeleteModal({ id: '', open: false })}
        data={{ id: openDeleteModal.id }}
      >
        <DeleteSpeciesModalContent
          id={openDeleteModal.id}
          handleCloseModal={handleDeleteCloseModal}
          handleDeleteFunction={handleDeleteFunction}
          isLoadingDelete={isLoadingDelete}
        />
      </CustomModal>
    </Box>
  );
});

SpeciesSection.displayName = 'SpeciesSection';

export default SpeciesSection;
